import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import uniq from 'lodash/uniq';

import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import DataTable from 'src/components/DataTable';
import { forceString, isDateInPast } from 'src/utils/datetime';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import {
  useGetUnavailabilities,
  useCancelUnavailabilities,
} from 'src/pages/CandidatesDetailsPage/api';

import type { CandidateTabProps } from 'src/pages/CandidatesDetailsPage/components/CandidateShiftsTab';

import messages from './messages';

export default function CandidateLeavesTab({ candidate }: CandidateTabProps) {
  const { formatMessage } = useIntl();

  const [params, setParams] = useState<Record<string, number>>({ page: 1 });

  const { data: unavailabilities, isLoading: loadingUnavailabilities } =
    useGetUnavailabilities(candidate?.id ?? '', params);
  const { mutate: cancelUnavailability } = useCancelUnavailabilities();

  const columns: Array<{
    id: string;
    label: string;
  }> = useMemo(
    () => [
      {
        id: 'kind',
        align: 'center',
        label: formatMessage(messages.typeColumnLabel),
        render: (
          kind: UCM.UnavailabilityKindType,
          { endsOn }: UCM.UnavailabilityType,
        ) => {
          const isPast = isDateInPast(endsOn);

          return (
            <Typography
              variant="inherit"
              color={isPast ? 'text.disabled' : 'inherit'}
            >
              {kind}
            </Typography>
          );
        },
      },
      {
        id: 'date',
        align: 'center',
        label: formatMessage(messages.dateColumnLabel),
        render: (_: Object, { startsOn, endsOn }: UCM.UnavailabilityType) => {
          const isPast = isDateInPast(endsOn);

          return (
            <Typography
              variant="inherit"
              color={isPast ? 'text.disabled' : 'inherit'}
            >
              {uniq([forceString(startsOn), forceString(endsOn)]).join(' - ')}
            </Typography>
          );
        },
      },
      {
        id: 'actions',
        label: '',
        width: 0,
        whiteSpace: 'nowrap',
        render: (_: Object, { uuid, endsOn }: UCM.UnavailabilityType) => {
          const isPast = isDateInPast(endsOn);

          return (
            <ConfirmationDialog
              maxWidth="sm"
              onConfirm={() => handleDeleteLeave(uuid)}
              message={formatMessage(messages.deleteLeaveConfirmMessage)}
            >
              {(handleOpen) => (
                <Tooltip
                  arrow
                  title={formatMessage(messages.deleteLeaveTooltip)}
                >
                  <span>
                    <IconButton onClick={handleOpen} disabled={isPast}>
                      <DeleteForeverOutlinedIcon
                        fontSize="small"
                        color={isPast ? 'primary' : 'inherit'}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </ConfirmationDialog>
          );
        },
      },
    ],
    [],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const handleDeleteLeave = useCallback((uuid: string) => {
    cancelUnavailability(uuid);
  }, []);

  return (
    <Grid container size={7} p={2}>
      <DataTable
        columns={columns}
        onPaginate={handlePagination}
        rows={unavailabilities?.data}
        loading={loadingUnavailabilities}
        pagination={unavailabilities?.meta}
      />
    </Grid>
  );
}
